// src/components/Atelier 1/Socle/RiskTreatmentGuideModal.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { X, HelpCircle, FileText, BarChart2, ShieldCheck, CheckSquare, Activity } from 'lucide-react';

const RiskTreatmentGuideModal = ({ isOpen, onClose, className = "" }) => {
  const { t } = useTranslation();
  
  if (!isOpen) return null;

  const steps = [
    {
      title: "Analyser les risques identifiés",
      description: "Examinez les événements redoutés et leur gravité initiale pour comprendre l'exposition aux risques de votre organisation.",
      icon: <BarChart2 size={20} className="text-blue-500" />
    },
    {
      title: "Évaluer l'efficacité des mesures",
      description: "Analysez l'efficacité des mesures de sécurité existantes et leur impact sur la réduction des risques résiduels.",
      icon: <ShieldCheck size={20} className="text-purple-500" />
    },
    {
      title: "Définir les options de traitement",
      description: "Pour chaque risque, choisissez une stratégie : réduire, transférer, éviter ou accepter selon votre contexte.",
      icon: <CheckSquare size={20} className="text-orange-500" />
    },
    {
      title: "Planifier les actions",
      description: "Établissez un plan d'action avec des responsables, des échéances et des critères de suivi pour chaque mesure.",
      icon: <FileText size={20} className="text-green-500" />
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className={`bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden mb-6 ${className}`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 flex justify-between items-center">
        <div className="flex items-center">
          <HelpCircle size={24} className="text-white mr-3" />
          <h2 className="text-xl font-bold text-white">Guide du Plan de Traitement</h2>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors duration-200"
          aria-label="Fermer le guide"
        >
          <X size={24} />
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="space-y-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex"
            >
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold">
                  {index + 1}
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  {step.icon}
                  <h3 className="text-lg font-semibold text-gray-800 ml-2">{step.title}</h3>
                </div>
                <p className="text-gray-600">{step.description}</p>
              </div>
            </motion.div>
          ))}

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md mt-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <Activity className="h-5 w-5 text-blue-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Conseil</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    Un plan de traitement efficace doit être adapté au contexte de votre organisation et régulièrement mis à jour selon l'évolution des menaces.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default RiskTreatmentGuideModal;
